import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi } from "@tanstack/react-router";
import { ApiError, batchReport, getBatch, retryFailedBatch } from "@/api/client";
import { useSession } from "@/auth/session";
import { BatchStats, ReportPanel } from "@/components/batch";
import { EmptyState, ErrorBanner, StatusBadge, ToolbarButton } from "@/components/ui";
import { BatchExportPanel } from "@/features/batches/BatchExport";
import { BatchPaymentsPanel } from "@/features/batches/BatchPayments";
import { EmailPanel } from "@/features/batches/EmailPanel";
import { PrintRunsPanel } from "@/features/batches/BatchPrint";
import { StartPrintRun } from "@/features/batches/StartPrintRun";
import { ISSUED_STATUSES, PRINTABLE_STATUSES } from "@/features/batches/statuses";

const detailApi = getRouteApi("/staff/batches/$batchId");

function PanelHeader({ subtitle, status }: { subtitle: string; status?: string }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-sm font-semibold">Generation report</h2>
        <p className="mt-1 text-xs text-zinc-500">{subtitle}</p>
      </div>
      {status ? <StatusBadge status={status} /> : null}
    </div>
  );
}

export function NoBatchSelected() {
  return (
    <>
      <PanelHeader subtitle="No batch selected" />
      <div className="mt-4">
        <EmptyState title="Select a batch" detail="Progress and failure details stay in this panel." compact />
      </div>
    </>
  );
}

export function BatchDetail() {
  const { batchId } = detailApi.useParams();
  const { can } = useSession();
  const queryClient = useQueryClient();

  // Fetched by id, not picked from the list, so a shared link works whatever the filter shows.
  const batch = useQuery({
    queryKey: ["batch", batchId],
    queryFn: () => getBatch(batchId),
    refetchInterval: (query) => (query.state.data?.status === "generating" ? 10_000 : false),
  });
  const generating = batch.data?.status === "generating";
  const report = useQuery({
    queryKey: ["batch-report", batchId],
    queryFn: () => batchReport(batchId),
    enabled: batch.isSuccess,
    refetchInterval: generating ? 10_000 : false,
  });
  const retry = useMutation({
    mutationFn: retryFailedBatch,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["batches"] });
      await queryClient.invalidateQueries({ queryKey: ["batch", batchId] });
      await queryClient.invalidateQueries({ queryKey: ["batch-report", batchId] });
    },
  });

  // 404 is an unknown id, 422 a malformed one; both mean the link points at nothing.
  if (batch.error instanceof ApiError && (batch.error.status === 404 || batch.error.status === 422)) {
    return (
      <>
        <PanelHeader subtitle={batchId} />
        <div className="mt-4">
          <EmptyState title="Batch not found" detail="No batch matches this link. Check the address or pick one from the list." compact />
        </div>
      </>
    );
  }

  if (!batch.data) {
    return (
      <>
        <PanelHeader subtitle={batchId} />
        <div className="mt-4 space-y-4">
          <ErrorBanner error={batch.error} />
          {batch.isLoading ? <EmptyState title="Loading batch" detail="Fetching batch details." compact /> : null}
        </div>
      </>
    );
  }

  return (
    <>
      <PanelHeader subtitle={batch.data.id} status={batch.data.status} />
      <div className="mt-4 space-y-4">
        <ErrorBanner error={report.error ?? retry.error} />
        <BatchStats batch={batch.data} />
        <ReportPanel report={report.data} loading={report.isLoading} />
        {can("payment.read") ? <BatchPaymentsPanel batchId={batch.data.id} /> : null}
        {ISSUED_STATUSES.includes(batch.data.status) ? (
          <>
            <BatchExportPanel batch={batch.data} />
            {can("certificate.print") && PRINTABLE_STATUSES.includes(batch.data.status) ? (
              <StartPrintRun batch={batch.data} />
            ) : null}
            <PrintRunsPanel batch={batch.data} />
            {can("certificate.read") && PRINTABLE_STATUSES.includes(batch.data.status) ? (
              <EmailPanel batch={batch.data} />
            ) : null}
          </>
        ) : null}
        <div className="flex flex-wrap items-center gap-3">
          <ToolbarButton
            disabled={retry.isPending || !["failed", "generating"].includes(batch.data.status)}
            onClick={() => retry.mutate(batch.data.id)}
          >
            Retry failed
          </ToolbarButton>
          {ISSUED_STATUSES.includes(batch.data.status) ? (
            <Link
              to="/certificates"
              search={{ batch: batch.data.id }}
              className="text-sm font-medium text-zinc-950 underline"
            >
              View certificates
            </Link>
          ) : null}
        </div>
      </div>
    </>
  );
}
